import { useState } from "react";
import { toast } from "sonner";
import { api, TOKEN_KEY } from "@/lib/leuteliaApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ShieldCheck, ArrowLeft, KeyRound, Mail } from "lucide-react";

export function AuthFrame({ title, sub, children, testid }) {
  return (
    <div className="min-h-screen bg-brand-cream leutelia-grain flex" data-testid={testid}>
      <div className="hidden lg:flex w-[46%] bg-brand-cream border-r border-brand-gold/30 flex-col justify-between p-12">
        <img src="/leutelia_logo.png" alt="LEUTELIA · Hotel Intelligence Studio" className="w-[300px] h-auto" data-testid="login-logo" />
        <div className="border-l-4 border-brand-maroon pl-5">
          <p className="font-serif italic text-[30px] text-brand-ink leading-snug">"Ospiti più soddisfatti, risultati più grandi."</p>
          <p className="text-[13px] text-brand-muted mt-3">LEUTELIA · Hotel Intelligence Studio</p>
        </div>
        <div className="text-[10.5px] font-bold tracking-[0.2em] text-brand-maroon">DATI CHE VALORIZZANO L'OSPITALITÀ</div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-[420px] leu-in">
          <div className="lg:hidden mb-8"><img src="/leutelia_logo.png" alt="LEUTELIA" className="w-[200px] h-auto" /></div>
          <div className="text-brand-orange font-bold tracking-[0.22em] text-[11px] uppercase">Accesso riservato</div>
          <h1 className="font-serif text-[30px] text-brand-ink leading-tight mt-2">{title}</h1>
          <p className="text-[13.5px] text-brand-muted mt-2">{sub}</p>
          <div className="mt-6 bg-white rounded-2xl border border-brand-gold/25 p-6 shadow-[0_1px_2px_rgba(15,58,45,0.04)]">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function Login({ onAuth }) {
  const [step, setStep] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [busy, setBusy] = useState(false);

  const login = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const r = await api.login({ email, password }); setChallenge(r); setStep("code"); toast.success(`Codice inviato a ${r.sent_to}`); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const verify = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const r = await api.verifyCode({ challenge_id: challenge.challenge_id, code }); localStorage.setItem(TOKEN_KEY, r.access_token); onAuth(r); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const [resent, setResent] = useState(false);
  const resend = async () => {
    setBusy(true);
    try { const r = await api.resendCode({ challenge_id: challenge.challenge_id }); setResent(true); toast.success(`Nuovo codice inviato a ${r.sent_to}`); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const forgot = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.forgotPassword({ email }); toast.success("Se l'email è registrata, riceverai il link per reimpostare la password."); setStep("credentials"); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  if (step === "code") return (
    <AuthFrame title="Controlla la tua email" sub={`Abbiamo inviato un codice a 6 cifre a ${challenge?.sent_to}. Vale 5 minuti.`} testid="login-code-step">
      <form onSubmit={verify} className="space-y-4">
        <label className="block text-[12.5px] font-semibold text-brand-ink">Codice di accesso
          <Input data-testid="login-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoFocus placeholder="••••••" className="mt-1 h-12 text-center font-serif text-[26px] tracking-[0.4em]" />
        </label>
        <Button type="submit" disabled={busy || code.length !== 6} data-testid="login-verify" className="w-full h-11 rounded-lg bg-brand-green hover:bg-brand-greenDark text-brand-cream text-[14px] font-semibold">
          {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <ShieldCheck size={16} className="mr-2" />} Entra
        </Button>
        <div className="rounded-lg bg-brand-cream/70 border border-brand-gold/25 p-3 text-[12.5px] text-brand-ink" data-testid="login-code-help">
          <strong>Non ricevi il codice?</strong> Controlla la cartella <em>Spam / Posta indesiderata</em> e attendi fino a un minuto. Il mittente è LEUTELIA.
          <button type="button" onClick={resend} disabled={busy} className="block mt-2 text-brand-green font-semibold hover:text-brand-gold disabled:opacity-50" data-testid="login-resend">{resent ? "Invia ancora un nuovo codice" : "Invia di nuovo il codice"}</button>
        </div>
        <button type="button" onClick={() => { setStep("credentials"); setCode(""); }} className="text-[12.5px] text-brand-muted hover:text-brand-green flex items-center gap-1" data-testid="login-back"><ArrowLeft size={13} /> Torna indietro</button>
      </form>
    </AuthFrame>
  );

  if (step === "forgot") return (
    <AuthFrame title="Password dimenticata" sub="Ti inviamo un link per sceglierne una nuova." testid="login-forgot-step">
      <form onSubmit={forgot} className="space-y-4">
        <label className="block text-[12.5px] font-semibold text-brand-ink">Email aziendale
          <Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 h-11" />
        </label>
        <Button type="submit" disabled={busy} data-testid="forgot-submit" className="w-full h-11 rounded-lg bg-brand-green hover:bg-brand-greenDark text-brand-cream text-[14px] font-semibold">
          {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Mail size={16} className="mr-2" />} Invia il link
        </Button>
        <button type="button" onClick={() => setStep("credentials")} className="text-[12.5px] text-brand-muted hover:text-brand-green flex items-center gap-1" data-testid="forgot-back"><ArrowLeft size={13} /> Torna all'accesso</button>
      </form>
    </AuthFrame>
  );

  return (
    <AuthFrame title="Benvenuto in LEUTELIA" sub="Accedi con la tua email aziendale. Per sicurezza ti chiederemo anche un codice inviato via email." testid="login-page">
      <form onSubmit={login} className="space-y-4">
        <label className="block text-[12.5px] font-semibold text-brand-ink">Email aziendale
          <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus placeholder="nome@tuohotel.it" className="mt-1 h-11" />
        </label>
        <label className="block text-[12.5px] font-semibold text-brand-ink">Password
          <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1 h-11" />
        </label>
        <Button type="submit" disabled={busy} data-testid="login-submit" className="w-full h-11 rounded-lg bg-brand-green hover:bg-brand-greenDark text-brand-cream text-[14px] font-semibold">
          {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <KeyRound size={16} className="mr-2" />} Continua
        </Button>
        <button type="button" onClick={() => setStep("forgot")} className="text-[12.5px] text-brand-muted hover:text-brand-green" data-testid="login-forgot">Password dimenticata?</button>
      </form>
    </AuthFrame>
  );
}
